import * as React from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

interface Breadcrumb {
  label: string;
  href?: string;
}

type Props = {
  breadcrumbs?: Breadcrumb[];     // Clickable breadcrumbs with optional href
  title?: string;                 // small subtitle (optional)
  headline?: string;              // big welcome line (optional)
  tabs?: string[];                // ["Tools","Documents","Messages"]
  activeTab?: string;
  onTabChange?: (t: string) => void;
  messageCount?: number;          // Badge count for Messages tab
  topOffset?: number;             // Kept for backward compatibility (unused)
  className?: string;
};

export const PageContextRow: React.FC<Props> = ({
  breadcrumbs = [{ label: "Home" }],
  title,
  headline,
  tabs = [],
  activeTab,
  onTabChange,
  messageCount = 0,
  topOffset = 64,
  className = "",
}) => {
  // Get page name from last breadcrumb (or "Snapshot" for home)
  // FIXED: Handle empty breadcrumbs array
  const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
  const pageName = lastBreadcrumb 
    ? (lastBreadcrumb.label === "Home" ? "Snapshot" : lastBreadcrumb.label)
    : "Snapshot";

  return (
    <div
      className={`h-10 px-6 flex items-center justify-between gap-4 bg-white/80 backdrop-blur-xl ${className}`}
      role="region"
      aria-label="Page context"
    >
      <div className="min-w-0 flex items-center gap-3">
       {/* Page name - subtle and minimal (hide if "Snapshot") */}
{pageName !== "Snapshot" && (
  <div className="text-[14px] font-medium text-slate-600">
    {pageName}
  </div>
)}
        
        {/* Breadcrumb - only show if breadcrumbs exist */}
        {breadcrumbs.length > 0 && (
          <>
            <div className="text-slate-300">•</div>
            <div className="flex items-center gap-1 text-xs text-slate-500">
              {breadcrumbs.map((crumb, i) => (
                <React.Fragment key={`${crumb.label}-${i}`}>
                  {crumb.href ? (
                    <Link 
                      to={crumb.href}
                      className="truncate hover:text-[#18365E] transition-colors"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="truncate">{crumb.label}</span>
                  )}
                  {i < breadcrumbs.length - 1 && <ChevronRight className="w-3 h-3 opacity-60" />}
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      {tabs.length > 0 && (
        <nav className="hidden md:flex items-center gap-2" aria-label="Section navigation">
          {tabs.map((tab) => {
            const active = tab === activeTab;
            const isMessages = tab === "Messages";
            const showBadge = isMessages && messageCount > 0;
            
            return (
              <button
                key={tab}
                onClick={() => onTabChange?.(tab)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18365E]
                  ${active ? "bg-[#18365E] text-white" : "text-[#18365E] hover:bg-black/5"}`}
                aria-current={active ? "page" : undefined}
              >
                {tab}
                {showBadge && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6E4C] text-[10px] font-bold text-white">
                    {messageCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
};